import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Global "current warehouse" filter applied across the app.
// null = all warehouses.
//
// Default policy: first-visit users get auto-seeded to the OVERSEAS warehouse
// once the list loads (see the seed effect in WarehouseSwitcher).
// `hasUserInteracted` distinguishes "user has never touched this control"
// from "user deliberately picked 'all'" — without it, the seed would either
// fight a user who picked all, or skip new users who happen to start at null.
interface WarehouseStore {
    currentWarehouseId: number | null;
    hasUserInteracted: boolean;
    setCurrentWarehouseId: (id: number | null) => void;
    /** Internal — used by the auto-seed effect to set the default OVERSEAS
     *  id without flipping `hasUserInteracted`. Don't call from UI. */
    seedDefaultWarehouseId: (id: number | null) => void;
}

export const useWarehouseStore = create<WarehouseStore>()(
    persist(
        (set) => ({
            currentWarehouseId: null,
            hasUserInteracted: false,
            setCurrentWarehouseId: (id) =>
                set({ currentWarehouseId: id, hasUserInteracted: true }),
            seedDefaultWarehouseId: (id) => set({ currentWarehouseId: id }),
        }),
        { name: 'daigou-current-warehouse' }
    )
);

/**
 * Convenience hook returning the current warehouseId or undefined.
 * Use undefined (not null) when passing as an API param so axios
 * omits the query parameter rather than sending `?warehouseId=null`.
 */
export function useCurrentWarehouseId(): number | undefined {
    const id = useWarehouseStore((s) => s.currentWarehouseId);
    return id ?? undefined;
}
